import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost from "apis/localhost";

import {
  ALL_PAGES_SCOPE,
  CONVERSATIONS_URL,
  HISTORY_URL,
  PAGES_URL,
} from "./endpoints";
import {
  conversationsToken,
  historyToken,
  openConversationToken,
} from "./requestToken";
import {
  selectLatestVisiConversationsRequestId,
  selectVisiEverywherePage,
} from "./selectors";
import {
  delegateNonVisiAxiosError,
  showVisiRequestFailed,
  VISI_REQUEST_CONFIG,
} from "./visiRequestError";

const conversationFrom = (data, fallbackId = null) => ({
  conversationId: data?.conversationId ?? fallbackId,
  title: data?.title ?? null,
  pageId: data?.pageId ?? null,
  pageTitle: data?.pageTitle ?? null,
  pageParams: data?.pageParams ?? {},
  turns: data?.turns || [],
});

export const fetchVisiEnabledPages = createAsyncThunk(
  "visiEverywhere/fetchEnabledPages",
  async (_, { rejectWithValue }) => {
    try {
      const rsp = await localhost.get(PAGES_URL, VISI_REQUEST_CONFIG);
      return rsp?.data || [];
    } catch (error) {
      console.warn("Failed to load the Visi Everywhere enabled pages", error);
      delegateNonVisiAxiosError(error);
      return rejectWithValue(null);
    }
  }
);

export const fetchVisiHistory = createAsyncThunk(
  "visiEverywhere/fetchHistory",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const page = selectVisiEverywherePage(state);
    if (!page?.pageId) {
      return rejectWithValue(null);
    }
    const token = historyToken(state);
    try {
      const rsp = await localhost.get(HISTORY_URL, {
        params: { ...(page.params || {}), pageId: page.pageId },
        ...VISI_REQUEST_CONFIG,
      });
      return { ...token, ...conversationFrom(rsp?.data) };
    } catch (error) {
      console.warn("Failed to restore the Visi Everywhere conversation", error);
      delegateNonVisiAxiosError(error);
      return rejectWithValue(token);
    }
  }
);

export const fetchVisiConversations = createAsyncThunk(
  "visiEverywhere/fetchConversations",
  async (
    allPages = false,
    { dispatch, getState, rejectWithValue, requestId }
  ) => {
    const state = getState();
    const page = selectVisiEverywherePage(state);
    if (!page?.pageId) {
      return rejectWithValue(null);
    }
    const token = conversationsToken(state);
    try {
      const rsp = await localhost.get(CONVERSATIONS_URL, {
        params: {
          ...(page.params || {}),
          pageId: page.pageId,
          ...(allPages ? { scope: ALL_PAGES_SCOPE } : {}),
        },
        ...VISI_REQUEST_CONFIG,
      });
      return { ...token, conversations: rsp?.data || [] };
    } catch (error) {
      console.warn("Failed to list the Visi Everywhere conversations", error);
      delegateNonVisiAxiosError(error);
      if (selectLatestVisiConversationsRequestId(getState()) === requestId) {
        showVisiRequestFailed(
          dispatch,
          "Your chats could not be loaded. Please try again."
        );
      }
      return rejectWithValue(null);
    }
  }
);

export const openVisiConversation = createAsyncThunk(
  "visiEverywhere/openConversation",
  async (conversationId, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const token = openConversationToken(state, conversationId);
    try {
      const rsp = await localhost.get(
        `${CONVERSATIONS_URL}/${conversationId}`,
        VISI_REQUEST_CONFIG
      );
      return { ...token, ...conversationFrom(rsp?.data, conversationId) };
    } catch (error) {
      console.warn("Failed to open the Visi Everywhere conversation", error);
      delegateNonVisiAxiosError(error);
      showVisiRequestFailed(
        dispatch,
        "This chat could not be opened. Please try again."
      );
      return rejectWithValue(token);
    }
  }
);
